"use client";

import * as React from "react";

import { Button } from "@unreserved/ui/components/button";
import { Input } from "@unreserved/ui/components/input";
import { Label } from "@unreserved/ui/components/label";
import { cn } from "@unreserved/utils/cn";
import { type VariantProps, cva } from "class-variance-authority";

import { EyeIcon, InfoWarningIcon } from "@/app/_components/icons";
import { EyeOffIcon } from "@/app/_components/icons/eye-off";

const inputVariants = cva(
  "flex w-full rounded-md border bg-white! p-3 text-sm text-typography-950! transition-all placeholder:text-typography-600! focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ring-0 focus-visible:ring-4 h-auto shadow-none",
  {
    variants: {
      variant: {
        default:
          "border-border-400 focus-visible:border-primary-500 focus-visible:ring-primary-200",
        error: "border-error-700 focus-visible:border-error-700 focus-visible:ring-error-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface FormInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: string;
  type?: "text" | "email" | "password" | "number" | "tel";
  showPasswordToggle?: boolean;
  prefix?: string;
  hideErrorIcon?: boolean;
  required?: boolean;
}

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      variant,
      label,
      error,
      type = "text",
      showPasswordToggle = true,
      disabled,
      prefix,
      hideErrorIcon: _hideErrorIcon = false,
      required: isRequired = false,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const isPassword = type === "password";
    const inputType = isPassword && showPassword ? "text" : type;

    const computedVariant = error ? "error" : variant;

    return (
      <div className={cn("w-full", className)}>
        {label && (
          <Label
            htmlFor={props.id || props.name}
            className="text-typography-900 mb-2 block rounded-md text-sm font-medium md:rounded-lg">
            {label}
            {isRequired && <span className="text-red-600">*</span>}
          </Label>
        )}
        <div
          className={cn(
            "relative flex items-center rounded-lg border transition-all",
            prefix &&
              "border-border-400 focus-within:border-primary-500 focus-within:ring-primary-200 bg-white focus-within:ring-4",
            !prefix && "border-0",
          )}>
          {prefix && (
            <>
              <span className="text-typography-600 px-3 text-sm">{prefix}</span>
              <div className="bg-border-400 h-6 w-px" />
            </>
          )}
          <div className={cn("relative", prefix ? "flex-1" : "w-full")}>
            <Input
              type={inputType}
              className={cn(
                inputVariants({ variant: computedVariant }),
                isPassword && showPasswordToggle && "pr-12",
                prefix && "rounded-l-none rounded-r-lg border-0 bg-transparent",
                className,
              )}
              ref={ref}
              disabled={disabled}
              aria-invalid={!!error}
              aria-describedby={error ? `${props.id || props.name}-error` : undefined}
              {...props}
            />
            {isPassword && showPasswordToggle && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowPassword(!showPassword)}
                className="text-muted-foreground focus:text-foreground absolute top-1/2 right-3 h-auto w-auto -translate-y-1/2 cursor-pointer p-0 hover:!bg-transparent"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}>
                {showPassword ? (
                  <EyeOffIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </Button>
            )}
          </div>
        </div>
        {error && (
          <div className="mt-1 flex flex-row items-center gap-1">
            {!_hideErrorIcon && <InfoWarningIcon width={13} height={13} />}
            <p
              id={`${props.id || props.name}-error`}
              className="text-error-600 text-sm"
              role="alert">
              {error}
            </p>
          </div>
        )}
      </div>
    );
  },
);

FormInput.displayName = "FormInput";

export { FormInput, inputVariants };
